import React, { useEffect, useState, useCallback } from "react";
import { api, API, errMsg, fmtNum, fmtMoney, fmtDate, fmtDay } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import StatusBadge, { SeverityBadge, RiskBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { Plus, RefreshCw, Loader2 } from "lucide-react";

const EXPORTABLE = new Set(["suppliers", "spas", "supplier-invoices", "supplier-payments",
  "lprc-transfers", "customers", "sales-orders", "delivery-orders", "deliveries",
  "reconciliations", "tanks", "exceptions"]);

function cellValue(row, col) {
  const v = col.get ? col.get(row) : row[col.key];
  switch (col.type) {
    case "num": return <span className="tabular">{fmtNum(v, col.dp || 0)}</span>;
    case "money": return <span className="tabular">{fmtMoney(v)}</span>;
    case "status": return <StatusBadge status={v} />;
    case "risk": return <RiskBadge level={v} />;
    case "sev": return <SeverityBadge level={v} />;
    case "date": return <span className="text-slate-500 text-xs">{fmtDate(v)}</span>;
    case "day": return <span className="text-slate-500 text-xs">{fmtDay(v)}</span>;
    case "mono": return <span className="mono text-xs">{v || "—"}</span>;
    default: return <span>{v ?? "—"}</span>;
  }
}

function FieldInput({ field, value, onChange }) {
  const [opts, setOpts] = useState(field.options || []);
  useEffect(() => {
    if (field.optionsSource) {
      api.get(`/${field.optionsSource.endpoint}`).then(({ data }) => {
        let rows = data;
        if (field.optionsSource.filter) rows = rows.filter(field.optionsSource.filter);
        setOpts(rows.map((r) => ({
          value: r[field.optionsSource.valueKey || "id"],
          label: field.optionsSource.label ? field.optionsSource.label(r) : r[field.optionsSource.labelKey || "name"],
        })));
      }).catch(() => {});
    }
  }, [field]);

  if (field.type === "select") {
    return (
      <Select value={value || ""} onValueChange={onChange}>
        <SelectTrigger data-testid={`field-${field.name}`}><SelectValue placeholder={field.placeholder || "Select…"} /></SelectTrigger>
        <SelectContent className="bg-white z-[60]">
          {opts.map((o) => (
            <SelectItem key={String(o.value)} value={String(o.value)}>{o.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }
  if (field.type === "textarea") {
    return <Textarea data-testid={`field-${field.name}`} value={value || ""} onChange={(e) => onChange(e.target.value)} />;
  }
  return (
    <Input
      data-testid={`field-${field.name}`}
      type={field.type === "number" ? "number" : field.type === "date" ? "date" : "text"}
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={field.placeholder}
    />
  );
}

function FormDialog({ open, onOpenChange, title, fields, onSubmit, submitLabel = "Save", testid, preview: Preview }) {
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setForm({}); }, [open]);

  const submit = async () => {
    for (const f of fields) {
      if (f.required && !form[f.name]) { toast.error(`${f.label} is required`); return; }
    }
    setBusy(true);
    try { await onSubmit(form); onOpenChange(false); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-head">{title}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-1 gap-3 py-2">
          {fields.map((f) => (
            <div key={f.name} className="grid gap-1.5">
              <Label className="text-xs font-medium text-slate-600">{f.label}{f.required && <span className="text-rose-500"> *</span>}</Label>
              <FieldInput field={f} value={form[f.name]} onChange={(v) => setForm((s) => ({ ...s, [f.name]: v }))} />
            </div>
          ))}
        </div>
        {Preview && <Preview form={form} />}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="btn-form-cancel">Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid={testid || "btn-form-submit"} className="bg-[#12346B] hover:bg-[#12346B]/90">
            {busy && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}{submitLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function CrudModule({ config }) {
  const { hasPerm } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [actionState, setActionState] = useState(null); // {row, action}

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get(`/${config.endpoint}`); setRows(data); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setLoading(false); }
  }, [config.endpoint]);

  useEffect(() => { load(); }, [load]);

  const doCreate = async (form) => {
    await api.post(`/${config.endpoint}`, form);
    toast.success(`${config.singular || "Record"} created`);
    load();
  };

  const runAction = async (row, action, body) => {
    try {
      await api.post(`/${config.endpoint}/${row.id}/${action.key}`, body || {});
      toast.success(action.toast || `${action.label} done`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const onActionClick = (row, action) => {
    if (action.inputs) setActionState({ row, action });
    else runAction(row, action);
  };

  const canCreate = !config.createPerm || hasPerm(config.createPerm);

  return (
    <div>
      <div className="flex items-start justify-between mb-4">
        <div>
          <h1 className="font-head text-2xl font-bold tracking-tight text-slate-900">{config.title}</h1>
          {config.subtitle && <p className="text-sm text-slate-500 mt-0.5">{config.subtitle}</p>}
        </div>
        <div className="flex items-center gap-2">
          {EXPORTABLE.has(config.endpoint) && (
            <div className="flex items-center gap-1 mr-1">
              {["csv", "xlsx", "pdf"].map((fmt) => (
                <a key={fmt} href={`${API}/export/${config.endpoint}?format=${fmt}`} target="_blank" rel="noreferrer"
                   data-testid={`export-${fmt}-${config.endpoint}`}
                   className="h-8 px-2 inline-flex items-center rounded-md border border-slate-200 text-[11px] font-semibold uppercase text-slate-600 hover:bg-slate-50">{fmt}</a>
              ))}
            </div>
          )}
          <Button variant="outline" size="sm" onClick={load} data-testid="btn-refresh"><RefreshCw className="h-4 w-4" /></Button>
          {config.createFields && canCreate && (
            <Button size="sm" onClick={() => setCreateOpen(true)} data-testid={`btn-create-${config.endpoint}`} className="bg-[#12346B] hover:bg-[#12346B]/90">
              <Plus className="h-4 w-4 mr-1" /> {config.createLabel || "New"}
            </Button>
          )}
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <Table className="pf-table">
            <TableHeader>
              <TableRow className="bg-slate-50 hover:bg-slate-50">
                {config.columns.map((c) => (
                  <TableHead key={c.key || c.label} className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold py-2.5 px-3">{c.label}</TableHead>
                ))}
                {config.actions && <TableHead className="text-right text-[11px] uppercase tracking-wide text-slate-500 font-semibold py-2.5 px-3">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={config.columns.length + 1} className="py-10 text-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin inline" /></TableCell></TableRow>
              ) : rows.length === 0 ? (
                <TableRow><TableCell colSpan={config.columns.length + 1} className="py-10 text-center text-slate-400 text-sm">No records yet.</TableCell></TableRow>
              ) : (
                rows.map((row) => {
                  const acts = config.actions ? config.actions(row).filter((a) => !a.perm || hasPerm(a.perm)) : [];
                  return (
                    <TableRow key={row.id} data-testid={`row-${config.endpoint}-${row.id}`} className="border-slate-100">
                      {config.columns.map((c) => (
                        <TableCell key={c.key || c.label} className="py-2 px-3 text-[13px] text-slate-700 align-middle">{cellValue(row, c)}</TableCell>
                      ))}
                      {config.actions && (
                        <TableCell className="py-2 px-3 text-right whitespace-nowrap">
                          {acts.length === 0 ? <span className="text-slate-300 text-xs">—</span> : acts.map((a) => (
                            <Button key={a.key} size="sm" variant={a.variant || "outline"}
                              data-testid={`btn-${a.key}-${row.id}`}
                              className={`h-7 px-2 ml-1 text-xs ${a.variant === "destructive" ? "" : a.primary ? "bg-[#12346B] hover:bg-[#12346B]/90 text-white border-0" : ""}`}
                              onClick={() => onActionClick(row, a)}>
                              {a.label}
                            </Button>
                          ))}
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {config.createFields && (
        <FormDialog open={createOpen} onOpenChange={setCreateOpen} title={config.createLabel || "New Record"}
          fields={config.createFields} onSubmit={doCreate} submitLabel="Create" testid={`btn-submit-${config.endpoint}`} preview={config.preview} />
      )}

      {actionState && (
        <FormDialog open={!!actionState} onOpenChange={(o) => !o && setActionState(null)}
          title={actionState.action.label} fields={actionState.action.inputs}
          submitLabel={actionState.action.label}
          onSubmit={async (form) => { await runAction(actionState.row, actionState.action, form); setActionState(null); }} />
      )}
    </div>
  );
}
